import React, { useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Nav from 'react-bootstrap/Nav';
import { NavLink, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';

/** Ein Eintrag der Sidebar-Navigation. */
interface SidebarNavItem {
  /** Ziel-Route (React Router Pfad). */
  readonly to: string;
  /** Deutscher Anzeigetext. */
  readonly label: string;
  /** Bootstrap-Icon-Klasse (z. B. "bi-speedometer2"). */
  readonly icon: string;
  /** Nur exakte Pfad-Uebereinstimmung als aktiv markieren (fuer "/"). */
  readonly end: boolean;
  /** Suffix fuer die HTML-IDs von Link, Icon und Label (z. B. "dashboard"). */
  readonly idSuffix: string;
}

const NAV_ITEMS: readonly SidebarNavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'bi-speedometer2', end: true, idSuffix: 'dashboard' },
  { to: '/benutzer', label: 'Benutzer', icon: 'bi-people', end: false, idSuffix: 'benutzer' },
  {
    to: '/ki-konfiguration',
    label: 'KI-Konfiguration',
    icon: 'bi-robot',
    end: false,
    idSuffix: 'ki-konfiguration',
  },
];

interface SidebarProps {
  /**
   * Praefix fuer alle HTML-IDs, damit die Sidebar mehrfach gerendert werden
   * kann (Desktop-Spalte und Mobile-Offcanvas), ohne IDs zu duplizieren.
   */
  readonly idPrefix: string;
  /** Callback nach Klick auf einen Navigationslink (z. B. Offcanvas schliessen). */
  readonly onNavigate?: () => void;
}

/**
 * Sidebar-Navigation mit den Bereichen Dashboard und Benutzer sowie
 * einem Footer mit angemeldetem Benutzer und Abmelden-Button.
 * Wird auf dem Desktop als feste Spalte und mobil im Offcanvas gerendert.
 */
function Sidebar({ idPrefix, onNavigate }: SidebarProps): React.JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback((): void => {
    onNavigate?.();
    logout();
    navigate('/login');
  }, [logout, navigate, onNavigate]);

  return (
    <div id={`${idPrefix}-wrapper`} className="d-flex flex-column flex-grow-1">
      <Nav
        as="nav"
        id={`${idPrefix}-nav`}
        className="sidebar-nav flex-column gap-1 px-2"
      >
        {NAV_ITEMS.map((item) => (
          <Nav.Link
            key={item.to}
            as={NavLink}
            to={item.to}
            end={item.end}
            id={`${idPrefix}-link-${item.idSuffix}`}
            onClick={onNavigate}
          >
            <i className={`bi ${item.icon}`} id={`${idPrefix}-icon-${item.idSuffix}`}></i>
            <span id={`${idPrefix}-label-${item.idSuffix}`}>{item.label}</span>
          </Nav.Link>
        ))}
      </Nav>

      {/* Footer: angemeldeter Benutzer + Abmelden */}
      <div
        id={`${idPrefix}-footer`}
        className="sidebar-footer mt-auto px-3 pt-3 border-top border-secondary"
      >
        <div
          id={`${idPrefix}-user-email`}
          className="text-truncate small mb-2"
          title={user?.email ?? ''}
        >
          <i className="bi bi-person-circle me-2" id={`${idPrefix}-user-icon`}></i>
          {user?.email ?? ''}
        </div>
        <Button
          id={`${idPrefix}-logout-button`}
          variant="outline-light"
          size="sm"
          className="w-100"
          onClick={handleLogout}
        >
          <i className="bi bi-box-arrow-right me-2" id={`${idPrefix}-logout-icon`}></i>
          Abmelden
        </Button>
      </div>
    </div>
  );
}

export default Sidebar;
